// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getIsPunchOutSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import acceptJobQuote from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/AcceptJobQuote";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@insite/content-library/Components/CartTotalDisplay";
import { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import { RfqQuoteDetailsPageContext } from "@insite/content-library/Pages/RfqQuoteDetailsPage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { ToastPresentationProps } from "@insite/mobius/Toast";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import declineQuote from "@mcmc/App/Handlers/Quotes/DeclineQuote";
import TwoButtonModal from "@mcmc/Overrides/Components/TwoButtonModal";
import EllipsisSpinner from "@mcmc/XDL/Components/Common/EllipsisSpinner";
import Notify from "@mcmc/XDL/Components/Notify";
import { commonCartButtonStyles } from "@mcmc/XDL/Utilities/sharedStyles";
import React, { FC, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    quoteState: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId),
    rfqMyQuotesPageUrl: getPageLinkByPageType(state, "RfqMyQuotesPage")?.url,
    checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
    cartState: getCurrentCartState(state),
    isPunchOutSession: getIsPunchOutSession(state),
    onePageCheckout: getSettingsCollection(state).cartSettings.onePageCheckout,
    onePageCheckoutPageUrl: getPageLinkByPageType(state, "OnePageCheckoutPage")?.url,
});

const mapDispatchToProps = {
    declineQuote,
    acceptJobQuote,
    doPunchOutCheckout,
};

type Props = WidgetProps & HasHistory & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface QuoteTotalStyles {
    cartTotal?: CartTotalDisplayStyles;
    declineQuoteButton?: ButtonPresentationProps;
    acceptQuoteButton?: ButtonPresentationProps;
    cartNotificationModal?: TwoButtonModalStyles;
    toastStyles?: ToastPresentationProps;
}

export const quoteTotalStyles: QuoteTotalStyles = {
    declineQuoteButton: commonCartButtonStyles("secondary"),
    acceptQuoteButton: commonCartButtonStyles("primary"),
    toastStyles: {
        cssOverrides: {
            toast: css`
                & {
                    width: 100%;
                    margin-top: 20px;
                    margin-bottom: 0;
                }
            `,
        },
    },
};

const styles = quoteTotalStyles;

const QuoteDetailsTotal: FC<Props> = ({
    quoteState,
    history,
    rfqMyQuotesPageUrl,
    checkoutShippingPageUrl,
    cartState,
    declineQuote,
    doPunchOutCheckout,
    isPunchOutSession,
    onePageCheckout,
    onePageCheckoutPageUrl,
}) => {
    const toasterContext = useContext(ToasterContext);
    const [cartNotificationModalIsOpen, setCartNotificationModalIsOpen] = useState(false);
    const [declineModalIsOpen, setDeclineModalIsOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const quote: any = quoteState.value;
    if (!quote) {
        return null;
    }

    const submitCartNotificationHandler = () => {
        redirectToCheckout();
    };

    const cancelCartNotificationHandler = () => {
        setCartNotificationModalIsOpen(false);
    };

    const submitDeclineHandler = () => {
        setIsSubmitting(true);
        setErrorMessage("");
        declineQuote({
            quoteNumber: quote.quoteNumber,
            onSuccess: () => {
                setIsSubmitting(false);
                toasterContext.addToast({ body: "Quote declined", messageType: "success" });
                redirectToMyQuotes();
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
            onError: error => {
                setIsSubmitting(false);
                setErrorMessage(error);
            },
        });
        setDeclineModalIsOpen(false);
    };

    const cancelDeclneHandler = () => {
        setDeclineModalIsOpen(false);
    };

    const declineQuoteClickHandler = () => {
        setDeclineModalIsOpen(true);
    };

    const acceptQuoteClickHandler = () => {
        if (cartState.value?.lineCount === 0) {
            redirectToCheckout();
        } else {
            setCartNotificationModalIsOpen(true);
        }
    };

    const redirectToMyQuotes = () => {
        if (rfqMyQuotesPageUrl) {
            history.push(rfqMyQuotesPageUrl);
        }
    };

    const redirectToCheckout = () => {
        if (onePageCheckout && onePageCheckoutPageUrl) {
            history.push(`${onePageCheckoutPageUrl}?cartId=${quote.id}`);
            return;
        }

        if (checkoutShippingPageUrl) {
            history.push(`${checkoutShippingPageUrl}?cartId=${quote.properties.quoteId}`);
        }
    };

    /* TODO find a way to test this after the backend changes get merged */
    const acceptQuoteWithPunchoutClickHandler = () => {
        doPunchOutCheckout({ quoteId: quote.properties.quoteId });
    };

    const declineQuoteLabel = translate(`Decline Quote`);
    const acceptQuoteLabel = translate(`Accept Quote & Checkout`);
    const acceptQuotePunchoutLabel = translate(`Return to Requisition`);
    const quoteExpired = quote.properties.isExpired === "True";
    const canBeDeclined =
        !quote.isSalesperson && !quoteExpired && (quote.status === "QuoteProposed" || isPunchOutSession);
    const canBeAccepted =
        !quote.isSalesperson &&
        !quoteExpired &&
        (quote.status === "QuoteProposed" || quote.status === "AwaitingApproval" || isPunchOutSession);

    return (
        <>
            <CartTotalDisplay cart={quote} showTaxAndShipping={false} extendedStyles={styles.cartTotal} />
            {(canBeAccepted || canBeDeclined) && (
                <StyledWrapper>
                    {!isPunchOutSession && canBeAccepted && (
                        <Button
                            {...styles.acceptQuoteButton}
                            onClick={acceptQuoteClickHandler}
                            data-test-selector="rfqQuoteDetails_acceptQuote"
                            disabled={isSubmitting}
                        >
                            {acceptQuoteLabel}
                        </Button>
                    )}
                    {isPunchOutSession && canBeAccepted && (
                        <Button
                            {...styles.acceptQuoteButton}
                            onClick={acceptQuoteWithPunchoutClickHandler}
                            data-test-selector="rfqQuoteDetails_acceptPunchoutQuote"
                            disabled={isSubmitting}
                        >
                            {acceptQuotePunchoutLabel}
                        </Button>
                    )}
                    {canBeDeclined && (
                        <Button
                            {...styles.declineQuoteButton}
                            onClick={declineQuoteClickHandler}
                            data-test-selector="rfqQuoteDetails_declineQuote"
                            disabled={isSubmitting}
                        >
                            <EllipsisSpinner
                                showSpinner={isSubmitting}
                                label={declineQuoteLabel}
                                typographyProps={styles.declineQuoteButton?.typographyProps}
                            ></EllipsisSpinner>
                        </Button>
                    )}
                    <TwoButtonModal
                        {...styles.cartNotificationModal}
                        modalIsOpen={declineModalIsOpen}
                        headlineText={translate("Decline Quote")}
                        messageText={siteMessage("McMc_Rfq_DeclineQuoteConfirmationMessage")}
                        cancelButtonText={translate("Cancel")}
                        submitButtonText={translate("Decline")}
                        onCancel={cancelDeclneHandler}
                        onSubmit={submitDeclineHandler}
                        submitTestSelector="rfqQuoteDetails_submitDeclineQuote"
                    />
                    <TwoButtonModal
                        {...styles.cartNotificationModal}
                        modalIsOpen={cartNotificationModalIsOpen}
                        headlineText={translate("Cart Notification")}
                        messageText={siteMessage("Rfq_CartNotificationMessage")}
                        cancelButtonText={translate("Cancel")}
                        submitButtonText={translate("Continue")}
                        onCancel={cancelCartNotificationHandler}
                        onSubmit={submitCartNotificationHandler}
                        submitTestSelector="submitCartNotification"
                    />
                </StyledWrapper>
            )}
            {errorMessage && (
                <Notify extendedStyles={styles.toastStyles} messageType="danger" hideCloseButton={true}>
                    {errorMessage}
                </Notify>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(QuoteDetailsTotal)),
    definition: {
        group: "RFQ Quote Details",
        allowedContexts: [RfqQuoteDetailsPageContext],
        displayName: "Quote Total",
    },
};

export default widgetModule;
